// src/layouts/dashboard/components/VendorMgtData/CreateUserModal.js
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Modal,
  Box,
  Grid,
  MenuItem,
  Select,
  Checkbox,
  Autocomplete,
  TextField,
  OutlinedInput,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";

import StorefrontIcon from "@mui/icons-material/Storefront";
import TuneIcon from "@mui/icons-material/Tune";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";

import { registerUser } from "api/apiService";
import { notifyCenter } from "utils/toast";
import CircularProgress from "@mui/material/CircularProgress";

const roles = [
  { value: "vendor", label: "Vendor" },
  { value: "vendor_staff", label: "Vendor-Staff" },
  { value: "customer", label: "Customer" },
  { value: "admin", label: "Admin" },
  { value: "admin_staff", label: "Admin-Staff" },
];

const emptyForm = {
  userName: "",
  first_name: "",
  last_name: "",
  user_email: "",
  password: "",
  role: "vendor",
  manage_vendor: true, // ✅ default checked

  phoneNumber: "",
  companyName: "",
  vatNumber: "",

  billing_first_name: "",
  billing_last_name: "",
  billing_company: "",
  billing_address_1: "",
  billing_address_2: "",
  billing_city: "",
  billing_state: "",
  billing_postcode: "",
  billing_country: "NO",
  billing_email: "",
  billing_phone: "",

  shipping_first_name: "",
  shipping_last_name: "",
  shipping_company: "",
  shipping_address_1: "",
  shipping_address_2: "",
  shipping_city: "",
  shipping_state: "",
  shipping_postcode: "",
  shipping_country: "NO",
  shipping_email: "",
  shipping_phone: "",

  latitude: "",
  longitude: "",
  google_calendar_id: "",
};

// ✅ Paste your COUNTRIES array here
const COUNTRIES = [
  { code: "AD", name: "Andorra" }, { code: "AE", name: "United Arab Emirates" },
  { code: "AF", name: "Afghanistan" }, { code: "AG", name: "Antigua and Barbuda" },
  { code: "AI", name: "Anguilla" }, { code: "AL", name: "Albania" },
  { code: "AM", name: "Armenia" }, { code: "AO", name: "Angola" },
  { code: "AQ", name: "Antarctica" }, { code: "AR", name: "Argentina" },
  { code: "AS", name: "American Samoa" }, { code: "AT", name: "Austria" },
  { code: "AU", name: "Australia" }, { code: "AW", name: "Aruba" },
  { code: "AX", name: "Åland Islands" }, { code: "AZ", name: "Azerbaijan" },
  { code: "BA", name: "Bosnia and Herzegovina" }, { code: "BB", name: "Barbados" },
  { code: "BD", name: "Bangladesh" }, { code: "BE", name: "Belgium" },
  { code: "BF", name: "Burkina Faso" }, { code: "BG", name: "Bulgaria" },
  { code: "BH", name: "Bahrain" }, { code: "BI", name: "Burundi" },
  { code: "BJ", name: "Benin" }, { code: "BL", name: "Saint Barthélemy" },
  { code: "BM", name: "Bermuda" }, { code: "BN", name: "Brunei Darussalam" },
  { code: "BO", name: "Bolivia (Plurinational State of)" },
  { code: "BQ", name: "Bonaire, Sint Eustatius and Saba" },
  { code: "BR", name: "Brazil" }, { code: "BS", name: "Bahamas" },
  { code: "BT", name: "Bhutan" }, { code: "BV", name: "Bouvet Island" },
  { code: "BW", name: "Botswana" }, { code: "BY", name: "Belarus" },
  { code: "BZ", name: "Belize" }, { code: "CA", name: "Canada" },
  { code: "CC", name: "Cocos (Keeling) Islands" },
  { code: "CD", name: "Congo, Democratic Republic of the" },
  { code: "CF", name: "Central African Republic" }, { code: "CG", name: "Congo" },
  { code: "CH", name: "Switzerland" }, { code: "CI", name: "Côte d'Ivoire" },
  { code: "CK", name: "Cook Islands" }, { code: "CL", name: "Chile" },
  { code: "CM", name: "Cameroon" }, { code: "CN", name: "China" },
  { code: "CO", name: "Colombia" }, { code: "CR", name: "Costa Rica" },
  { code: "CU", name: "Cuba" }, { code: "CV", name: "Cabo Verde" },
  { code: "CW", name: "Curaçao" }, { code: "CX", name: "Christmas Island" },
  { code: "CY", name: "Cyprus" }, { code: "CZ", name: "Czechia" },
  { code: "DE", name: "Germany" }, { code: "DJ", name: "Djibouti" },
  { code: "DK", name: "Denmark" }, { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" }, { code: "DZ", name: "Algeria" },
  { code: "EC", name: "Ecuador" }, { code: "EE", name: "Estonia" },
  { code: "EG", name: "Egypt" }, { code: "EH", name: "Western Sahara" },
  { code: "ER", name: "Eritrea" }, { code: "ES", name: "Spain" },
  { code: "ET", name: "Ethiopia" }, { code: "FI", name: "Finland" },
  { code: "FJ", name: "Fiji" }, { code: "FK", name: "Falkland Islands (Malvinas)" },
  { code: "FM", name: "Micronesia (Federated States of)" },
  { code: "FO", name: "Faroe Islands" }, { code: "FR", name: "France" },
  { code: "GA", name: "Gabon" }, { code: "GB", name: "United Kingdom of Great Britain and Northern Ireland" },
  { code: "GD", name: "Grenada" }, { code: "GE", name: "Georgia" },
  { code: "GF", name: "French Guiana" }, { code: "GG", name: "Guernsey" },
  { code: "GH", name: "Ghana" }, { code: "GI", name: "Gibraltar" },
  { code: "GL", name: "Greenland" }, { code: "GM", name: "Gambia" },
  { code: "GN", name: "Guinea" }, { code: "GP", name: "Guadeloupe" },
  { code: "GQ", name: "Equatorial Guinea" }, { code: "GR", name: "Greece" },
  { code: "GS", name: "South Georgia and the South Sandwich Islands" },
  { code: "GT", name: "Guatemala" }, { code: "GU", name: "Guam" },
  { code: "GW", name: "Guinea-Bissau" }, { code: "GY", name: "Guyana" },
  { code: "HK", name: "Hong Kong" }, { code: "HM", name: "Heard Island and McDonald Islands" },
  { code: "HN", name: "Honduras" }, { code: "HR", name: "Croatia" },
  { code: "HT", name: "Haiti" }, { code: "HU", name: "Hungary" },
  { code: "ID", name: "Indonesia" }, { code: "IE", name: "Ireland" },
  { code: "IM", name: "Isle of Man" }, { code: "IN", name: "India" },
  { code: "IO", name: "British Indian Ocean Territory" }, { code: "IQ", name: "Iraq" },
  { code: "IR", name: "Iran (Islamic Republic of)" }, { code: "IS", name: "Iceland" },
  { code: "IT", name: "Italy" }, { code: "JE", name: "Jersey" },
  { code: "JM", name: "Jamaica" }, { code: "JO", name: "Jordan" },
  { code: "JP", name: "Japan" }, { code: "KE", name: "Kenya" },
  { code: "KG", name: "Kyrgyzstan" }, { code: "KH", name: "Cambodia" },
  { code: "KI", name: "Kiribati" }, { code: "KM", name: "Comoros" },
  { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "KP", name: "Korea (Democratic People's Republic of)" },
  { code: "KR", name: "Korea, Republic of" }, { code: "KW", name: "Kuwait" },
  { code: "KY", name: "Cayman Islands" }, { code: "KZ", name: "Kazakhstan" },
  { code: "LA", name: "Lao People's Democratic Republic" }, { code: "LB", name: "Lebanon" },
  { code: "LC", name: "Saint Lucia" }, { code: "LI", name: "Liechtenstein" },
  { code: "LK", name: "Sri Lanka" }, { code: "LR", name: "Liberia" },
  { code: "LS", name: "Lesotho" }, { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" }, { code: "LV", name: "Latvia" },
  { code: "LY", name: "Libya" }, { code: "MA", name: "Morocco" },
  { code: "MC", name: "Monaco" }, { code: "MD", name: "Moldova, Republic of" },
  { code: "ME", name: "Montenegro" }, { code: "MF", name: "Saint Martin (French part)" },
  { code: "MG", name: "Madagascar" }, { code: "MH", name: "Marshall Islands" },
  { code: "MK", name: "North Macedonia" }, { code: "ML", name: "Mali" },
  { code: "MM", name: "Myanmar" }, { code: "MN", name: "Mongolia" },
  { code: "MO", name: "Macao" }, { code: "MP", name: "Northern Mariana Islands" },
  { code: "MQ", name: "Martinique" }, { code: "MR", name: "Mauritania" },
  { code: "MS", name: "Montserrat" }, { code: "MT", name: "Malta" },
  { code: "MU", name: "Mauritius" }, { code: "MV", name: "Maldives" },
  { code: "MW", name: "Malawi" }, { code: "MX", name: "Mexico" },
  { code: "MY", name: "Malaysia" }, { code: "MZ", name: "Mozambique" },
  { code: "NA", name: "Namibia" }, { code: "NC", name: "New Caledonia" },
  { code: "NE", name: "Niger" }, { code: "NF", name: "Norfolk Island" },
  { code: "NG", name: "Nigeria" }, { code: "NI", name: "Nicaragua" },
  { code: "NL", name: "Netherlands" }, { code: "NO", name: "Norway" },
  { code: "NP", name: "Nepal" }, { code: "NR", name: "Nauru" },
  { code: "NU", name: "Niue" }, { code: "NZ", name: "New Zealand" },
  { code: "OM", name: "Oman" }, { code: "PA", name: "Panama" },
  { code: "PE", name: "Peru" }, { code: "PF", name: "French Polynesia" },
  { code: "PG", name: "Papua New Guinea" }, { code: "PH", name: "Philippines" },
  { code: "PK", name: "Pakistan" }, { code: "PL", name: "Poland" },
  { code: "PM", name: "Saint Pierre and Miquelon" }, { code: "PN", name: "Pitcairn" },
  { code: "PR", name: "Puerto Rico" }, { code: "PS", name: "Palestine, State of" },
  { code: "PT", name: "Portugal" }, { code: "PW", name: "Palau" },
  { code: "PY", name: "Paraguay" }, { code: "QA", name: "Qatar" },
  { code: "RE", name: "Réunion" }, { code: "RO", name: "Romania" },
  { code: "RS", name: "Serbia" }, { code: "RU", name: "Russian Federation" },
  { code: "RW", name: "Rwanda" }, { code: "SA", name: "Saudi Arabia" },
  { code: "SB", name: "Solomon Islands" }, { code: "SC", name: "Seychelles" },
  { code: "SD", name: "Sudan" }, { code: "SE", name: "Sweden" },
  { code: "SG", name: "Singapore" }, { code: "SH", name: "Saint Helena, Ascension and Tristan da Cunha" },
  { code: "SI", name: "Slovenia" }, { code: "SJ", name: "Svalbard and Jan Mayen" },
  { code: "SK", name: "Slovakia" }, { code: "SL", name: "Sierra Leone" },
  { code: "SM", name: "San Marino" }, { code: "SN", name: "Senegal" },
  { code: "SO", name: "Somalia" }, { code: "SR", name: "Suriname" },
  { code: "SS", name: "South Sudan" }, { code: "ST", name: "Sao Tome and Principe" },
  { code: "SV", name: "El Salvador" }, { code: "SX", name: "Sint Maarten (Dutch part)" },
  { code: "SY", name: "Syrian Arab Republic" }, { code: "SZ", name: "Eswatini" },
  { code: "TC", name: "Turks and Caicos Islands" }, { code: "TD", name: "Chad" },
  { code: "TF", name: "French Southern Territories" }, { code: "TG", name: "Togo" },
  { code: "TH", name: "Thailand" }, { code: "TJ", name: "Tajikistan" },
  { code: "TK", name: "Tokelau" }, { code: "TL", name: "Timor-Leste" },
  { code: "TM", name: "Turkmenistan" }, { code: "TN", name: "Tunisia" },
  { code: "TO", name: "Tonga" }, { code: "TR", name: "Türkiye" },
  { code: "TT", name: "Trinidad and Tobago" }, { code: "TV", name: "Tuvalu" },
  { code: "TW", name: "Taiwan, Province of China" }, { code: "TZ", name: "Tanzania, United Republic of" },
  { code: "UA", name: "Ukraine" }, { code: "UG", name: "Uganda" },
  { code: "UM", name: "United States Minor Outlying Islands" },
  { code: "US", name: "United States of America" }, { code: "UY", name: "Uruguay" },
  { code: "UZ", name: "Uzbekistan" }, { code: "VA", name: "Holy See" },
  { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "VE", name: "Venezuela (Bolivarian Republic of)" },
  { code: "VG", name: "Virgin Islands (British)" },
  { code: "VI", name: "Virgin Islands (U.S.)" }, { code: "VN", name: "Viet Nam" },
  { code: "VU", name: "Vanuatu" }, { code: "WF", name: "Wallis and Futuna" },
  { code: "WS", name: "Samoa" }, { code: "YE", name: "Yemen" },
  { code: "YT", name: "Mayotte" }, { code: "ZA", name: "South Africa" },
  { code: "ZM", name: "Zambia" }, { code: "ZW", name: "Zimbabwe" },
  // extras
  { code: "XK", name: "Kosovo" }, { code: "UK", name: "United Kingdom" },
  { code: "EU", name: "European Union" }, { code: "AC", name: "Ascension Island" },
  { code: "TA", name: "Tristan da Cunha" }, { code: "IC", name: "Canary Islands" },
  { code: "EA", name: "Ceuta and Melilla" },
];

function CountrySelect({ label, value, onChange }) {
  const selected = useMemo(() => COUNTRIES.find((c) => c.code === value) || null, [value]);

  return (
    <SoftBox mb={1.5}>
      <SoftTypography component="label" variant="caption" fontWeight="bold">
        {label}
      </SoftTypography>
      <Autocomplete
        options={COUNTRIES}
        value={selected}
        onChange={(_, v) => onChange(v ? v.code : "")}
        getOptionLabel={(o) => (o?.name ? o.name : "")}
        isOptionEqualToValue={(o, v) => o.code === v.code}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            placeholder="Velg land"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                height: 42,
                minHeight: 42,
                paddingRight: "36px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "10px 12px",
              },
              "& .MuiAutocomplete-endAdornment": {
                right: 8,
              },
            }}
          />
        )}
        ListboxProps={{ style: { maxHeight: 320 } }}
      />
    </SoftBox>
  );
}

CountrySelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default function CreateUserModal({ open, onClose, onCreated }) {
  const [f, setF] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const selectRef = useRef(null);

  const [open1, setOpen1] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPass, setShowPass] = useState(true);

  // ✅ Advanced toggle (contact + addresses)
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (open) {
      setShowAdvanced(false);
      setF(emptyForm); // reset form on open
    }
  }, [open]);

  const handleInputClick1 = () => setOpen1((prevOpen) => !prevOpen);
  const handleClose = () => setOpen1(false);

  const isVendor = useMemo(() => !!f.manage_vendor, [f.manage_vendor]);

  const set = (field) => (e) => {
    const value = e?.target?.value ?? e;
    setF((s) => ({ ...s, [field]: value }));
  };

  const handleUsernameChange = (e) => {
    const v = e.target.value || "";
    const cleaned = v
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9._-]/g, "");
    setF((s) => ({ ...s, userName: cleaned }));
  };

  const validate = () => {
    const e = {};
    ["userName", "first_name", "last_name", "user_email", "password", "role"].forEach((k) => {
      if (!String(f[k] || "").trim()) e[k] = "Påkrevd";
    });

    if (isVendor) {
      if (!String(f.latitude || "").trim()) e.latitude = "Påkrevd";
      if (!String(f.longitude || "").trim()) e.longitude = "Påkrevd";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      notifyCenter("error", "Fyll ut alle påkrevde felter.", { duration: 2500 });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...f,
        latitude: isVendor ? f.latitude : "",
        longitude: isVendor ? f.longitude : "",
        google_calendar_id: isVendor ? f.google_calendar_id : "",
      };

      const res = await registerUser(payload);
      if (res?.status) {
        notifyCenter("success", res?.message || "Leverandør opprettet", { duration: 2000 });
        setF(emptyForm);
        onClose?.();
        onCreated?.();
      } else {
        notifyCenter("error", res?.message || "Klarte ikke å opprette leverandør", { duration: 3000 });
      }
    } catch (err) {
      notifyCenter("error", "Klarte ikke å opprette leverandør", { duration: 3000 });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const onVendorChange = (_e, checked) => setF((s) => ({ ...s, manage_vendor: checked }));

  const field = (name, label, props = {}) => (
    <SoftBox mb={1.5}>
      <SoftTypography component="label" variant="caption" fontWeight="bold">
        {label} {errors[name] && <span style={{ color: "#d32f2f" }}>*</span>}
      </SoftTypography>
      <SoftInput value={f[name]} onChange={(e) => set(name)(e)} error={Boolean(errors[name])} {...props} />
    </SoftBox>
  );

  // ✅ Advanced checkbox row
  const AdvancedCheckboxRow = (
    <SoftBox
      role="checkbox"
      aria-checked={showAdvanced}
      tabIndex={0}
      onClick={() => setShowAdvanced((s) => !s)}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          setShowAdvanced((s) => !s);
        }
      }}
      sx={{
        mt: 2,
        mb: 1,
        borderRadius: "12px",
        cursor: "pointer",
        border: (t) => `1px solid ${showAdvanced ? t.palette.info.main : t.palette.divider}`,
        backgroundColor: (t) => (showAdvanced ? `${t.palette.info.main}14` : t.palette.background.default),
        px: 2,
        py: 1.25,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        userSelect: "none",
      }}
    >
      <SoftBox display="flex" alignItems="center" gap={1.25}>
        <SoftBox
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            backgroundColor: (t) => t.palette.info.main,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <TuneIcon fontSize="small" />
        </SoftBox>

        <SoftTypography variant="button" fontWeight="bold">
          Avansert (Kontakt og adresser – valgfritt)
        </SoftTypography>
      </SoftBox>

      <Checkbox
        checked={showAdvanced}
        onClick={(e) => e.stopPropagation()}
        onChange={(_e, checked) => setShowAdvanced(checked)}
        icon={<CheckBoxOutlineBlankRoundedIcon />}
        checkedIcon={<CheckBoxRoundedIcon />}
        disableRipple
        sx={{
          "& .MuiSvgIcon-root": { fontSize: 26 },
          color: (t) => (showAdvanced ? t.palette.info.main : t.palette.text.secondary),
          "&.Mui-checked": { color: (t) => t.palette.info.main },
        }}
      />
    </SoftBox>
  );

  // ✅ IS VENDOR checkbox row (same as user modal)
  const VendorCheckboxRow = (
    <SoftBox
      role="checkbox"
      aria-checked={isVendor}
      tabIndex={0}
      onClick={() => onVendorChange(null, !isVendor)}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onVendorChange(null, !isVendor);
        }
      }}
      sx={{
        mt: 2,
        mb: 1,
        borderRadius: "12px",
        cursor: "pointer",
        border: (t) => `1px solid ${isVendor ? t.palette.info.main : t.palette.divider}`,
        backgroundColor: (t) => (isVendor ? `${t.palette.info.main}14` : t.palette.background.default),
        px: 2,
        py: 1.25,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        userSelect: "none",
      }}
    >
      <SoftBox display="flex" alignItems="center" gap={1.25}>
        <SoftBox
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            backgroundColor: (t) => t.palette.info.main,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <StorefrontIcon fontSize="small" />
        </SoftBox>

        <SoftTypography variant="button" fontWeight="bold">
          Er leverandør (merk av for å vise leverandør-felter)
        </SoftTypography>
      </SoftBox>

      <Checkbox
        checked={isVendor}
        onClick={(e) => e.stopPropagation()}
        onChange={onVendorChange}
        icon={<CheckBoxOutlineBlankRoundedIcon />}
        checkedIcon={<CheckBoxRoundedIcon />}
        disableRipple
        sx={{
          "& .MuiSvgIcon-root": { fontSize: 26 },
          color: (t) => (isVendor ? t.palette.info.main : t.palette.text.secondary),
          "&.Mui-checked": { color: (t) => t.palette.info.main },
        }}
      />
    </SoftBox>
  );

  return (
    <Modal open={open} onClose={saving ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 1050,
          maxHeight: "85vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: "12px",
        }}
        aria-busy={isLoading}
      >
        {/* FULL-FORM LOADER OVERLAY */}
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.92)",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={72} color="info" />
          </Box>
        )}

        <SoftTypography variant="h5" mb={2}>
          Ny leverandør
        </SoftTypography>

        {/* Konto */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {field("userName", "Brukernavn *", { onChange: handleUsernameChange })}
          </Grid>

          <Grid item xs={12} md={6}>
            <SoftBox mb={1.5}>
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Rolle *
              </SoftTypography>

              <Select
                displayEmpty
                value={f.role || ""}
                onChange={(e) => set("role")(e)}
                input={<OutlinedInput onClick={handleInputClick1} />}
                sx={{ height: "40px !important", cursor: "pointer" }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography
                        variant="body1"
                        style={{ fontSize: "14px", fontWeight: "normal", margin: "-11px", color: "#aab1c3" }}
                      >
                        Rolle
                      </Typography>
                    );
                  }
                  const selectedItem = roles.find((r) => r.value === selected);
                  return (
                    <Typography variant="body1" style={{ fontSize: "14px", fontWeight: "normal", margin: "-11px" }}>
                      {selectedItem?.label}
                    </Typography>
                  );
                }}
                inputProps={{ "aria-label": "Rolle" }}
                ref={selectRef}
                open={open1}
                onClose={handleClose}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen1(!open1);
                }}
              >
                <MenuItem disabled value="">
                  <Typography variant="body1" style={{ fontSize: "14px", fontWeight: "normal" }}>
                    <em>Rolle</em>
                  </Typography>
                </MenuItem>

                {roles.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    <Typography variant="body1" style={{ fontSize: "14px", fontWeight: "normal" }}>
                      {r.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </SoftBox>
          </Grid>

          <Grid item xs={12} md={6}>
            {field("first_name", "Fornavn *")}
          </Grid>
          <Grid item xs={12} md={6}>
            {field("last_name", "Etternavn *")}
          </Grid>

          <Grid item xs={12} md={6}>
            {field("user_email", "E-post *")}
          </Grid>

          <Grid item xs={12} md={6}>
            <SoftBox mb={1.5}>
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Passord * {errors.password && <span style={{ color: "#d32f2f" }}>*</span>}
              </SoftTypography>

              <TextField
                fullWidth
                size="small"
                value={f.password}
                onChange={(e) => set("password")(e)}
                type={showPass ? "text" : "password"}
                placeholder="Passord"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPass((s) => !s)} edge="end" size="small" tabIndex={-1}>
                        {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    padding: "12px 14px !important",
                    "& input": { width: "calc(100% - 0px) !important" },
                  },
                }}
                error={Boolean(errors.password)}
              />
            </SoftBox>
          </Grid>
        </Grid>

        {/* ✅ Advanced toggle */}
        {AdvancedCheckboxRow}

        {/* ✅ Advanced sections (optional) */}
        {showAdvanced && (
          <>
            <SoftTypography variant="h6" mt={2} mb={1}>
              Kontakt
            </SoftTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {field("phoneNumber", "Telefon")}
              </Grid>
              <Grid item xs={12} md={3}>
                {field("companyName", "Firma")}
              </Grid>
              <Grid item xs={12} md={3}>
                {field("vatNumber", "MVA-nummer")}
              </Grid>
            </Grid>

            <SoftTypography variant="h6" mt={2} mb={1}>
              Fakturaadresse
            </SoftTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {field("billing_first_name", "Fornavn")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("billing_last_name", "Etternavn")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("billing_company", "Firma")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("billing_address_1", "Adresse 1")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("billing_address_2", "Adresse 2")}
              </Grid>
              <Grid item xs={12} md={3}>
                {field("billing_city", "By")}
              </Grid>
              <Grid item xs={12} md={3}>
                {field("billing_state", "Stat")}
              </Grid>
              <Grid item xs={12} md={3}>
                {field("billing_postcode", "Postnummer")}
              </Grid>
              <Grid item xs={12} md={3}>
                <CountrySelect
                  label="Land"
                  value={f.billing_country}
                  onChange={(code) => setF((s) => ({ ...s, billing_country: code }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {field("billing_email", "E-post")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("billing_phone", "Telefon")}
              </Grid>
            </Grid>

            <SoftTypography variant="h6" mt={2} mb={1}>
              Leveringsadresse
            </SoftTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {field("shipping_first_name", "Fornavn")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("shipping_last_name", "Etternavn")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("shipping_company", "Firma")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("shipping_address_1", "Adresse 1")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("shipping_address_2", "Adresse 2")}
              </Grid>
              <Grid item xs={12} md={3}>
                {field("shipping_city", "By")}
              </Grid>
              <Grid item xs={12} md={3}>
                {field("shipping_state", "Stat")}
              </Grid>
              <Grid item xs={12} md={3}>
                {field("shipping_postcode", "Postnummer")}
              </Grid>
              <Grid item xs={12} md={3}>
                <CountrySelect
                  label="Land"
                  value={f.shipping_country}
                  onChange={(code) => setF((s) => ({ ...s, shipping_country: code }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {field("shipping_email", "E-post")}
              </Grid>
              <Grid item xs={12} md={6}>
                {field("shipping_phone", "Telefon")}
              </Grid>
            </Grid>
          </>
        )}

        {/* ✅ Is vendor checkbox row */}
        {VendorCheckboxRow}

        {/* Vendor-spesifikt */}
        {isVendor && (
          <>
            <SoftTypography variant="h6" mt={1} mb={1}>
              Leverandør-spesifikt
            </SoftTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                {field("latitude", "Latitude *")}
              </Grid>
              <Grid item xs={12} md={4}>
                {field("longitude", "Longitude *")}
              </Grid>
              <Grid item xs={12} md={4}>
                {field("google_calendar_id", "Google Calendar ID")}
              </Grid>
            </Grid>
          </>
        )}

        <SoftBox display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
          <SoftButton disabled={saving} variant="outlined" color="info" onClick={onClose}>
            AVBRYT
          </SoftButton>

          <SoftButton
            disabled={saving}
            variant="gradient"
            color="info"
            onClick={submit}
            sx={{ position: "relative", minWidth: 120 }}
          >
            <span style={{ opacity: saving ? 0 : 1 }}>LAGRE</span>

            {saving && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <CircularProgress size={20} color="white" />
              </Box>
            )}
          </SoftButton>
        </SoftBox>
      </Box>
    </Modal>
  );
}

CreateUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};
